import React, { useEffect, useState } from "react"
import { Button, Col, Row } from "reactstrap"
import axios from "axios"
import { api } from "../../utils/variables"
import TopAppCardM from "../MetaInsight/TopAppCardM"
import "./preOrders.css"
import TopAppCardSkeleton from "./TopAppCardSkeleton"
import FilterPanel from "../../components/KeywordTrackingNewUIPage/FilterPanel"
import { useFilters } from "../../hooks/useFilters"
import { useUrlPlatform } from "../../hooks/useUrlPlatform"
import InsightsPageHeader from "../../components/MetaInsight/InsightsPageHeader"
import PlatformsApi from "../../services/platformsApi"
import SEO from "../../components/Common/SEO"
import { PAGE_SEO } from "../../config/seoConfig"
import { generateCanonicalUrl, applySEODate } from "../../utils/seoUtils"

export default function PreOrdersPage() {
  const { filterSections } = useFilters()
  const [preOrders, setPreOrders] = useState([])
  const [platforms, setPlatforms] = useState([])
  // Lives in the `?platform=` URL query param (defaulting to Meta) rather than
  // plain state, so it survives a round trip to an app's detail page and back,
  // and so a direct link can pre-select a platform.
  const [selectedPlatform, setSelectedPlatform] = useUrlPlatform('meta')
  const [platformsLoading, setPlatformsLoading] = useState(true)
  const [itemsToShow, setItemsToShow] = useState(8)
  const [showAll, setShowAll] = useState(false)
  const [loading, setLoading] = useState(true)
  const [selectedCardId, setSelectedCardId] = useState(null)

  // country/language + timestamp
  const [countriesList, setCountriesList] = useState([])
  const [countryOptions, setCountryOptions] = useState([])
  const [countriesLoading, setCountriesLoading] = useState(true)
  const [preOrdersCountry, setPreOrdersCountry] = useState(() => {
    try {
      return sessionStorage.getItem('preOrders_selectedCountry') || 'US'
    } catch (e) {
      return 'US'
    }
  })
  const [preOrdersLanguage, setPreOrdersLanguage] = useState(() => {
    try {
      return sessionStorage.getItem('preOrders_selectedLanguage')
    } catch (e) {
      return null
    }
  })
  const [preOrdersLastUpdated, setPreOrdersLastUpdated] = useState(null)
  const [preOrdersFilterPanelOpen, setPreOrdersFilterPanelOpen] = useState(false)
  const [preOrdersAppliedFilters, setPreOrdersAppliedFilters] = useState({})
  const [sortedItems, setSortedItems] = useState([])
  const [isSorted, setIsSorted] = useState(false)
  const [rankSort, setRankSort] = useState(null)

  const preCache = sessionStorage.getItem("preOrderMeta")

  useEffect(() => {
    // load countries first then fetch
    const loadCountries = async () => {
      setCountriesLoading(true)
      setPlatformsLoading(true)
      let list = []
      try {
        const [countriesRes, fetchedPlatforms] = await Promise.all([
          axios.get(`${api}/countries/`),
          PlatformsApi.fetchPlatforms()
        ])
        const res = countriesRes  // keep rest of logic using `res`
        list = res.data?.data || []
        setCountriesList(list)
        setCountryOptions(list.map(c => ({ value: c.code, label: c.name })))
        setPlatforms(fetchedPlatforms || PlatformsApi.getDefaultPlatforms())
        setPlatformsLoading(false)
        const us = list.find(x => x.code === 'US')
        const defaultLocale = us && Array.isArray(us.locales) && us.locales.length > 0 ? us.locales[0] : null
        setPreOrdersLanguage(prev => prev || defaultLocale)
      } catch (err) {
        console.error('Error loading countries:', err)
        setPlatforms(PlatformsApi.getDefaultPlatforms())
        setPlatformsLoading(false)
      } finally {
        setCountriesLoading(false)
        const initCountry = preOrdersCountry || (list && list.length > 0 ? list[0].code : 'US')
        const countryEntry = list.find(x => x.code === initCountry)
        const initLangFromList = countryEntry && Array.isArray(countryEntry.locales) && countryEntry.locales.length > 0 ? countryEntry.locales[0] : null
        const initLanguage = preOrdersLanguage || initLangFromList || null
        setPreOrdersCountry(prev => prev || initCountry)
        setPreOrdersLanguage(prev => prev || initLanguage)

        // restore persisted filters (survive route changes)
        let savedFilters = null
        try {
          const raw = sessionStorage.getItem('preOrdersAppliedFilters')
          savedFilters = raw ? JSON.parse(raw) : null
        } catch (err) {
          savedFilters = null
        }
        setPreOrdersAppliedFilters(savedFilters || {})

        setLoading(true)
        if (preCache && (!initCountry && !initLanguage)) {
          setPreOrders(JSON.parse(preCache))
          setLoading(false)
        } else {
          fetchPreOrders(initCountry, initLanguage, 1, savedFilters || {})
        }
      }
    }

    loadCountries()
  }, [])

  // Country/language select rendering (flags, react-select styling) now lives in
  // InsightsPageHeader; this page only needs to resolve a language when the country
  // changes, so its fetch call gets the right locale.
  const handlePreOrdersCountryChange = (code, langForCountry) => {
    setPreOrdersCountry(code)
    setPreOrdersLanguage(langForCountry)
    setLoading(true)
    fetchPreOrders(code, langForCountry, 1, preOrdersAppliedFilters)
  }

  const handlePreOrdersLanguageChange = (val) => {
    setPreOrdersLanguage(val)
    try {
      sessionStorage.setItem('preOrders_selectedLanguage', val || '')
    } catch (e) { /* ignore */ }
    setLoading(true)
    fetchPreOrders(preOrdersCountry, val, 1, preOrdersAppliedFilters)
  }

  const handlePlatformChange = (platformId) => {
    setSelectedPlatform(platformId)
    fetchPreOrders(preOrdersCountry, preOrdersLanguage, 1, preOrdersAppliedFilters, platformId)
  }

  const fetchPreOrders = async (
    country = preOrdersCountry,
    language = preOrdersLanguage,
    page = 1,
    filters = preOrdersAppliedFilters,
    platform = selectedPlatform
  ) => {
    // placeholder: preorders does not maintain explicit page state here

    if (preCache && !country && !language) {
      setPreOrders(JSON.parse(preCache))
      setLoading(false)
      return
    }

    try {
      // Re-arm the skeleton placeholder for every fetch (platform switch included) —
      // not just the initial mount — so the grid doesn't sit stale while new data loads.
      setLoading(true)
      const params = {}
      if (country) params.country = country
      if (language) params.language = language
      if (page) params.page = page

      if (filters && typeof filters === 'object') {
        if (filters.price) params.price = Array.isArray(filters.price) ? filters.price.join(',') : filters.price
        if (filters.rating) params.rating = Array.isArray(filters.rating) ? filters.rating.join(',') : filters.rating
        if (filters.genre) params.genre = Array.isArray(filters.genre) ? filters.genre.join(',') : filters.genre
        if (filters.sort_by) params.sort_by = filters.sort_by
        if (filters.sort) params.sort_by = params.sort_by || filters.sort
        if (filters.bundle) params.bundle = Array.isArray(filters.bundle) ? filters.bundle.join(',') : filters.bundle
        if (filters.number_of_players) params.number_of_players = filters.number_of_players
      }

      console.info('fetchPreOrders called', { params })
      const token = localStorage.getItem("authToken")
      const headers = {}
      if (token) headers.Authorization = `Bearer ${token}`
      const res = await axios.get(`${api}/tracker/stats/${platform}/coming_soon_pre_orders`, { params, headers })
      const games = res.data.games || []
      setPreOrders(games)
      try { sessionStorage.setItem("preOrderMeta", JSON.stringify(games)) } catch (e) { /* ignore */ }
      setPreOrdersLastUpdated(new Date())
    } catch (error) {
      console.error("Error fetching pre orders:", error)
    } finally {
      setLoading(false)
    }
  }



  useEffect(() => {
    if (showAll) setItemsToShow(preOrders.length)
    else setItemsToShow(8)
  }, [showAll, preOrders.length])

  const handleCardClick = cardId => {
    setSelectedCardId(cardId)
  }

  const displayApps = isSorted ? sortedItems.slice(0, itemsToShow) : preOrders.slice(0, itemsToShow)

  const parseRank = (app) => {
    if (!app) return Number.MAX_SAFE_INTEGER
    const candidates = [app.rank, app.position, app.ranking, app.rank_position, app.rankValue]
    for (let c of candidates) {
      if (c !== undefined && c !== null && c !== '' && !isNaN(Number(c))) return Number(c)
    }
    if (app.meta && (app.meta.rank !== undefined)) return Number(app.meta.rank)
    return Number.MAX_SAFE_INTEGER
  }

  const sortByRank = (order = 'asc') => {
    const arr = Array.isArray(preOrders) ? [...preOrders] : []
    arr.sort((a, b) => {
      const ra = parseRank(a)
      const rb = parseRank(b)
      return order === 'asc' ? (ra - rb) : (rb - ra)
    })
    setSortedItems(arr)
    setIsSorted(true)
  }

  const toggleRankSort = () => {
    const next = rankSort === 'asc' ? 'desc' : 'asc'
    setRankSort(next)
    sortByRank(next)
  }

  useEffect(() => {
    if (!rankSort) { setIsSorted(false); setSortedItems([]); return }
    sortByRank(rankSort)
  }, [preOrders, rankSort])

  const handlePreOrdersFilterApply = (filters) => {
    setPreOrdersAppliedFilters(filters)
    // persist filters so they survive route changes
    try { sessionStorage.setItem('preOrdersAppliedFilters', JSON.stringify(filters || {})) } catch (e) { /* ignore */ }
    // Reset to first page for filter application
    setLoading(true)
    fetchPreOrders(preOrdersCountry, preOrdersLanguage, 1, filters)
    setPreOrdersFilterPanelOpen(false)
  }

  const handleRemovePreOrdersFilter = (key) => {
    const newFilters = { ...(preOrdersAppliedFilters || {}) }
    if (Object.prototype.hasOwnProperty.call(newFilters, key)) {
      delete newFilters[key]
      setPreOrdersAppliedFilters(newFilters)
      // persist updated filters and show skeleton while re-fetching
      try { sessionStorage.setItem('preOrdersAppliedFilters', JSON.stringify(newFilters || {})) } catch (e) { /* ignore */ }
      setLoading(true)
      fetchPreOrders(preOrdersCountry, preOrdersLanguage, 1, newFilters)
    }
  }

  // Description/disclaimer reference whichever store is currently selected, rather
  // than always saying "Meta" regardless of the active platform tab.
  const platformLabel = PlatformsApi.getPlatformDisplayName(selectedPlatform, platforms)
  const platformIconUrl = PlatformsApi.getPlatformIcon(selectedPlatform, platforms)

  return (
    <React.Fragment>
      <SEO
        title={applySEODate(PAGE_SEO.preOrders.title)}
        description={PAGE_SEO.preOrders.description}
        keywords={PAGE_SEO.preOrders.keywords}
        canonical={generateCanonicalUrl('/pre-orders')}
        robots={PAGE_SEO.preOrders.robots}
      />
      <div className="container-fluid preorders-page" style={{ maxWidth: "100vw", padding: 32 }}>
        <div className="category_track_innersec">
          <div className="row">
            <div className="col-sm-12">
              <InsightsPageHeader
                title="Pre-Orders – Coming Soon"
                description={`Browse the top pre-order apps available this week on the ${platformLabel} Store — and be first to experience them.`}
                disclaimer={`Insights are based on ${platformLabel} Store rankings and verified pre-order data.`}
                platforms={platforms}
                platformsLoading={platformsLoading}
                selectedPlatform={selectedPlatform}
                onPlatformChange={handlePlatformChange}
                countriesList={countriesList}
                countryOptions={countryOptions}
                countriesLoading={countriesLoading}
                selectedCountry={preOrdersCountry}
                selectedLanguage={preOrdersLanguage}
                onCountryChange={handlePreOrdersCountryChange}
                onLanguageChange={handlePreOrdersLanguageChange}
                appliedFilters={preOrdersAppliedFilters}
                onRemoveFilter={handleRemovePreOrdersFilter}
                lastUpdated={preOrdersLastUpdated}
                onOpenFilters={() => setPreOrdersFilterPanelOpen(true)}
                rankSort={rankSort}
                onToggleSort={toggleRankSort}
              />
            </div>
          </div>

          <div className="preorders-content">
            {loading ? (
              <Row className="g-3">
                {Array.from({ length: 4 }).map((_, index) => (
                  <Col key={index} xs={12} sm={6}>
                    <div className="preorders-card">
                      <TopAppCardSkeleton />
                    </div>
                  </Col>
                ))}
              </Row>
            ) : (
              <>
                {displayApps.length > 0 ? (
                  <Row className="g-3">
                    {displayApps.map((app, index) => (
                      <Col key={app?.item_id || index} xs={12} sm={6}>
                        <div
                          className={`preorders-card ${selectedCardId === app?.item_id ? "selected" : ""}`}
                          onClick={() => handleCardClick(app?.item_id)}
                        >
                          {/* Pre-order apps haven't launched yet, so there's no real revenue or
                              downloads figure to show — hide both fields instead of a misleading
                              fallback. "Released" (a future date) still makes sense to show. */}
                          <TopAppCardM gamedata={app} priceLayout="discounts" showEnds={false} showRevenue={false} showDownloads={false} headingLevel="h2" platformIcon={platformIconUrl} platformName={platformLabel} />
                        </div>
                      </Col>
                    ))}
                  </Row>
                ) : (
                  <div style={{ textAlign: 'center', padding: 40 }}>
                    <p style={{ color: '#A1ACB8', fontSize: 16 }}>
                      {Object.keys(preOrdersAppliedFilters || {}).length > 0
                        ? 'No results matched with filter.'
                        : 'No results to display.'}
                    </p>
                  </div>
                )}

                {!loading && (
                  <div className="text-center mt-4">
                    {!showAll && preOrders.length > 4 ? (
                      <Button className="see-more-btn" onClick={() => setShowAll(true)}>
                        See More
                      </Button>
                    ) : showAll ? (
                      <Button className="see-less-btn" onClick={() => setShowAll(false)}>
                        See Less
                      </Button>
                    ) : null}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <FilterPanel
        open={preOrdersFilterPanelOpen}
        sections={filterSections.length > 0 ? filterSections : undefined}
        // Platform is already selectable via the Meta Quest/Steam tabs above -
        // don't duplicate it inside the filter panel.
        hidePlatformSection
        onClose={() => setPreOrdersFilterPanelOpen(false)}
        onApply={handlePreOrdersFilterApply}
        initialValues={preOrdersAppliedFilters}
      />
    </React.Fragment>
  )
}


